
import { Product } from "@/lib/types";
import { CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ShoppingCart } from "lucide-react";
import { calculateDaysOfSupply, calculatePackagesToOrder, needsRestocking } from "@/lib/storage/stock/helpers";
import { getProgressColor, getProgressValue } from "./utils/product-status";

interface ProductCardContentProps {
  product: Product;
}

const ProductCardContent = ({ product }: ProductCardContentProps) => {
  const daysRemaining = calculateDaysOfSupply(product);
  const needsRestock = needsRestocking(product);
  const packagesToOrder = calculatePackagesToOrder(product);
  const progressColor = getProgressColor(daysRemaining, product.minStockAlert);
  const progressValue = getProgressValue(daysRemaining, product.minStockAlert);
  
  return (
    <CardContent className="pb-2">
      {product.description && (
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
          {product.description}
        </p>
      )}
      
      <div className="space-y-3">
        <div className="flex justify-between text-sm">
          <span className="text-gray-500 dark:text-gray-400">Current Stock:</span>
          <span className="font-medium">{product.currentStock} units</span>
        </div>
        
        <div className="flex justify-between text-sm">
          <span className="text-gray-500 dark:text-gray-400">Daily Usage:</span>
          <span className="font-medium">
            {product.actualUsageRate 
              ? `${product.actualUsageRate.toFixed(1)} units`
              : `${product.usageRate.toFixed(1)} units (estimated)`}
          </span>
        </div>
        
        {product.packageSize && product.packageSize > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-500 dark:text-gray-400">Package Size:</span>
            <span className="font-medium">{product.packageSize} units/pack</span>
          </div>
        )}
        
        <div className="flex justify-between text-sm">
          <span className="text-gray-500 dark:text-gray-400">Alert Threshold:</span>
          <span className="font-medium">{product.minStockAlert} days</span>
        </div>
        
        <div className="space-y-1">
          <span className="text-sm text-gray-500 dark:text-gray-400">Supply Status:</span>
          <div className={`h-2 w-full rounded-full overflow-hidden ${progressColor === "bg-red-500" ? "bg-red-100" : progressColor === "bg-yellow-500" ? "bg-yellow-100" : "bg-teal-100"}`}>
            <Progress value={progressValue} className="h-2" />
          </div>
        </div>
        
        {needsRestock && product.packageSize && product.packageSize > 0 && packagesToOrder > 0 && (
          <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-md">
            <div className="flex items-center text-sm text-blue-700 dark:text-blue-300">
              <ShoppingCart className="h-4 w-4 mr-2" />
              <span>
                Suggested order: <strong>{packagesToOrder} {packagesToOrder === 1 ? 'package' : 'packages'}</strong>
                {' '}({packagesToOrder * product.packageSize} units)
              </span>
            </div>
          </div>
        )}
      </div>
    </CardContent>
  );
};

export default ProductCardContent;
